/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {batch} from 'react-redux';
import {errorEditOptions, successEditOptions} from '@ion/applications/src/components/prompts/constants';
import {toggleUserPrompt} from '@ion/applications/src/store/actions/user-prompt';
import {hasAccountIds} from '@ion/core/src/entities/Accounts';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {destroyGeoFences, loadGeofences, updateGeoFences} from '@ion/core/src/services/ArcGIS';
import {
  Entity,
  GeoFenceAttributes
} from '@ion/core/src/types/entities';
import {GeofenceSearchableProperty, LoadGeofencesParams} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {translatePromptAction} from '@ion/dls/src/utils/ComponentUtils';
import {PageInitializeAction} from '@/types/redux';
import {getAccountId, getGeofenceAccountIds} from '../helpers';

// name of the reducer
export const NAME                             = 'adminGeofences';

export const INITIALIZE_ADMIN_GEOFENCES       = 'INITIALIZE_ADMIN_GEOFENCES';
export const RE_INITIALIZE_ADMIN_GEOFENCES    = 'RE_INITIALIZE_ADMIN_GEOFENCES';

// grid column state
export const SET_GRID_COLUMNS                 = 'SET_ADMIN_GEOFENCES_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS              = 'UPDATE_ADMIN_GEOFENCES_GRID_COLUMNS';

export const GET_ADMIN_GEOFENCES_REQUEST      = 'GET_ADMIN_GEOFENCES_REQUEST';
export const GET_ADMIN_GEOFENCES_RESPONSE     = 'GET_ADMIN_GEOFENCES_RESPONSE';
export const GET_ADMIN_GEOFENCES_ERROR        = 'GET_ADMIN_GEOFENCES_ERROR';

// geofence add ...
export const ADD_ADMIN_GEOFENCES              = 'ADD_ADMIN_GEOFENCES';
export const ADD_ADMIN_GEOFENCES_REQUEST      = 'ADD_ADMIN_GEOFENCES_REQUEST';
export const ADD_ADMIN_GEOFENCES_RESPONSE     = 'ADD_ADMIN_GEOFENCES_RESPONSE';
export const ADD_ADMIN_GEOFENCES_ERROR        = 'ADD_ADMIN_GEOFENCES_ERROR';

// geofence edit ...
export const EDIT_ADMIN_GEOFENCE              = 'EDIT_ADMIN_GEOFENCE';

// geofence delete ...
export const DELETE_ADMIN_GEOFENCE            = 'DELETE_ADMIN_GEOFENCE';

export const ADMIN_GEOFENCES_GRID_REFRESHED   = 'ADMIN_GEOFENCES_GRID_REFRESHED';

// View map
export const SET_MAP_INITIALIZED              = 'SET_MAP_INITIALIZED';
export const SET_MAP_BASEMAP                  = 'SET_MAP_BASEMAP';
export const SET_MAP_EXTENT                   = 'SET_MAP_EXTENT';
export const SET_MAP_ZOOM                     = 'SET_MAP_ZOOM';

// bulk geofences edit ...
export const EDIT_ADMIN_GEOFENCES              = 'EDIT_ADMIN_GEOFENCES';
export const EDIT_ADMIN_GEOFENCES_REQUEST      = 'EDIT_ADMIN_GEOFENCES_REQUEST';
export const EDIT_ADMIN_GEOFENCES_RESPONSE     = 'EDIT_ADMIN_GEOFENCES_RESPONSE';
export const EDIT_ADMIN_GEOFENCES_ERROR        = 'EDIT_ADMIN_GEOFENCES_ERROR';

// bulk geofence deletes ...
export const DELETE_ADMIN_GEOFENCES          = 'DELETE_ADMIN_GEOFENCES';
export const DELETE_ADMIN_GEOFENCES_REQUEST  = 'DELETE_ADMIN_GEOFENCES_REQUEST';
export const DELETE_ADMIN_GEOFENCES_RESPONSE = 'DELETE_ADMIN_GEOFENCES_RESPONSE';
export const DELETE_ADMIN_GEOFENCES_ERROR    = 'DELETE_ADMIN_GEOFENCES_ERROR';

// admin geofence filter model ...
export const SET_ADMIN_GEOFENCES_GRID_FILTER_MODEL = 'SET_ADMIN_GEOFENCES_GRID_FILTER_MODEL';

/**
 * @initialize
 */
export const initializeAdminGeofences = (): PageInitializeAction => ({
  type : INITIALIZE_ADMIN_GEOFENCES
});

export const reInitializeAdminGeofences = (): PageInitializeAction => ({
  type : RE_INITIALIZE_ADMIN_GEOFENCES
});

/**
 * @editAdminGeofence
 *
 * Invoked when the Edit Admin Geofence editor is to be displayed or removed.
 */
export const editAdminGeofence = (geofence: GeoFenceAttributes, show = true) => ({
  type : EDIT_ADMIN_GEOFENCE, geofence, show
});

/**
 * @adminGeofencesGridRefreshed
 */
export const adminGeofencesGridRefreshed = (): PageInitializeAction => ({
  type : ADMIN_GEOFENCES_GRID_REFRESHED
});

/**
 * @formatSortParam
 */
export const formatSortParam = (sort) => {
  let sortParam = '';
  const item    = sort?.[0];
  if (item) {
    sortParam = `${GeofenceSearchableProperty[item]}`;
    if (item.match(/\+/)) {
      const prop = item.replace('+', '');
      sortParam = `${GeofenceSearchableProperty[prop]} asc`;
    } else if (item.match(/\-/)) {
      const prop = item.replace('-', '');
      sortParam = `${GeofenceSearchableProperty[prop]} desc`;
    }
  }
  return sortParam;
};

/**
 * @getAdminGeofences
 */
export const getAdminGeofences = ({
  sort,
  filter,
  ancestorAccountIds,
  pageId = 0,
  pageSize = 20,
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_GEOFENCES_REQUEST});
    const data: LoadGeofencesParams = {
      accountIds        : includeSubGroups ? getGeofenceAccountIds(getState) : [getAccountId(getState)],
      resultOffset      : pageId * pageSize,
      resultRecordCount : pageSize,
      ancestorAccountIds
    };
    if (sort) {
      data.sortOn = formatSortParam(sort);
    }
    if (filter) {
      data.filterOn = filter;
    }
    try {
      // skips the request when there are no account ids to query (e.g. the global group is still loading)...
      const {geofences: items, totalCount} = hasAccountIds(data.accountIds)
        ? await loadGeofences(data)
        : {geofences: [], totalCount: 0};
      dispatch({type: GET_ADMIN_GEOFENCES_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      dispatch({type: GET_ADMIN_GEOFENCES_ERROR, error: err});
      return err;
    }
  };
};

export const setMapInitialized = () => ({
  type : SET_MAP_INITIALIZED
});

export const setBasemap = (basemap: any) => ({
  type : SET_MAP_BASEMAP, basemap
});

export const setExtent = (extent: any) => ({
  type : SET_MAP_EXTENT, extent
});

export const setZoom = (zoom: number) => ({
  type : SET_MAP_ZOOM, zoom
});

/**
 * Invoked when the Bulk Edit Admin Geofences Dialog is to be displayed or removed.
 */
export const editAdminGeofences = (geofences: GeoFenceAttributes[], show = true) => ({
  type : EDIT_ADMIN_GEOFENCES, geofences, show
});

/**
 * Invoked when a collection of existing geofences are to be updated
 * and dispatching a successful response or error action
 * accordingly.
 */
export const editGeofences = (geofences: GeoFenceAttributes[], isReadOnlyUser: boolean = false) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_GEOFENCES_REQUEST});
    try {
      const {results, errors} = await updateGeoFences(geofences);
      if (!errors.length) {
        batch(() => {
          dispatch(toggleUserPrompt(successEditOptions(translate('common:entityLabels.geofences', {
            results, items : geofences
          }))));
          dispatch({type: EDIT_ADMIN_GEOFENCES_RESPONSE, geofences: results});
        });
      } else {
        const error = errors[0];
        dispatch({type: EDIT_ADMIN_GEOFENCES_ERROR, error});
        dispatch(toggleUserPrompt({
          ...errorEditOptions(Entity.geofence, error),
          ...({actionLabel: translatePromptAction('common:retry', isReadOnlyUser)})
        }));
      }
    } catch (err) {
      batch(() => {
        dispatch({type: EDIT_ADMIN_GEOFENCES_ERROR, error: err});
        dispatch(toggleUserPrompt(errorEditOptions(translate('common:entityLabels.geofences'))));
      });
      error(err);
    }
  };
};

/**
 * @deleteAdminGeofence
 *
 * Invoked when the Delete Admin Geofence Dialog is to be displayed or removed.
 */
export const deleteAdminGeofence = (geofence: GeoFenceAttributes, show = true) => ({
  type : DELETE_ADMIN_GEOFENCE, geofence, show
});

/**
 * Invoked when the Bulk delete Admin Geofences Dialog is to be displayed or removed.
 */
export const deleteAdminGeofences = (geofences: GeoFenceAttributes[], show = true) => ({
  type : DELETE_ADMIN_GEOFENCES, geofences, show
});

/**
 * Invoked on confirmation to delete Bulk Admin Geofences.
*/
export const deleteGeoFences = (geofences: GeoFenceAttributes[]) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_GEOFENCES_REQUEST});
    try {
      const {results, errors} = await destroyGeoFences(geofences);
      if (!errors.length) {
        dispatch({type: DELETE_ADMIN_GEOFENCES_RESPONSE, geofences: results});
      } else {
        dispatch({type: DELETE_ADMIN_GEOFENCES_ERROR, error: errors[0]});
      }
    } catch (err) {
      dispatch({type: DELETE_ADMIN_GEOFENCES_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the Bulk delete Admin Geofences error to be set or unset in state.
 */
export const removeGeofencesError = (error) => ({
  type : DELETE_ADMIN_GEOFENCES_ERROR, error : error
});

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_ADMIN_GEOFENCES_GRID_FILTER_MODEL, model
});
